import React, {useContext, useEffect, useRef, useState} from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import {CloudDownloadOutlined, RadioButtonCheckedOutlined, SendToMobileOutlined} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import {Input, ALL_FORMATS, UrlSource} from "mediabunny";
import {useAssetStore} from "../../storage/AssetStore";
import {
    LibrettoProjectTypes,
    IsVideoTrackType,
    LibrettoRecordingMode
} from "../../utils/utils";
import { fitToCanvas, maybeLockCanvasToFirstVideo } from "../../utils/canvasSizing";
import {ProjectAssetsStore} from "../../state/ProjectAssetsStore";
import {CompositionService} from "../../librettoEditor/services/CompositionService";
import {CompositionStateStore} from "../../librettoEditor/store/CompositionStateStore";
import SelectableCardButton from "../NewHome/SelectableCard";
import {RecordSomethingMenu} from "../NewHome/ProjectContextMenu";
import {PAGE_MODES} from "./V2Editor";
import {uploadFilesWithPoolLimit} from "../../utils/multiFileUpload";
import {CreateScriptClipForComposition} from "./utils";
import type {ScriptLayerInputConfig} from "../../engine/clips/script/script.interfaces";
import { trackSessionEvent } from "../../analytics/sessionEvents";
import { AuthContext } from "../../context/AuthContext";
import { CombineFilesDialog } from "./components/CombineFilesDialog";
import { deriveSequenceTitleFromFiles, populateSequenceFromTracks } from "./sequenceAutoCreate";
import { UnsupportedFilesDialog, UnsupportedFile } from "../../components/UnsupportedFilesDialog";
import { validateFilesForUpload } from "../../utils/fileValidation";
import { insertTrackIntoScriptLayer } from "./scriptInsertion/insertTrackIntoScriptLayer";
import MobileUploadDialog from "./components/MobileUploadDialog";

// Default colors for ScriptClip sources (should match transcript + waveform palettes)
const DEFAULT_SOURCE_COLORS = ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

type Props = {
    projectId: string;
    compositionId?: string;
    setPageMode: (val: string) => void;
    setRecordingMode: (num: number) => {};
    setIsScreenRecordingMode: (val: boolean) => {};
    openProjectSidebar?: () => void;
    onImportFromZoom?: () => void;
};

const CreateCompositionView: React.FC<Props> = ({projectId, compositionId, setPageMode, setRecordingMode, setIsScreenRecordingMode, openProjectSidebar, onImportFromZoom}) => {
    const {UploadAsset, UploadNonLocalFirstAsset, GetTrackUrl, CreateSequence, RenameComposition} = useAssetStore();
    const authContext = useContext(AuthContext);
    const navigate = useNavigate();
    const [title, setTitle] = useState<string>("");
    const viewRootRef = useRef<HTMLDivElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [pendingAssetIds, setPendingAssetIds] = useState<string[]>([]);
    const recordButtonRef = useRef<HTMLDivElement | null>(null);
    const [recordMenuOpen, setRecordMenuOpen] = useState<boolean>(false);
    const [mobileUploadDialogOpen, setMobileUploadDialogOpen] = useState<boolean>(false);
    const [recordMenuPos, setRecordMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

    // State for combine files dialog (shown when multiple files are selected)
    const [combineDialogOpen, setCombineDialogOpen] = useState<boolean>(false);
    const [pendingFiles, setPendingFiles] = useState<Array<{ file: File }>>([]);
    
    // Track uploaded asset IDs for multi-file uploads (to know when to create sequence/clips)
    const [multiUploadAssets, setMultiUploadAssets] = useState<Array<{ assetId: string; fileName: string; uploadIndex: number }>>([]);
    const [multiUploadMode, setMultiUploadMode] = useState<'separate' | 'sequence' | null>(null);
    const [sequenceIdForMultiUpload, setSequenceIdForMultiUpload] = useState<string | null>(null);
    const [sequenceTitleForMultiUpload, setSequenceTitleForMultiUpload] = useState<string | null>(null);
    const multiUploadProcessingRef = useRef(false);
    const [expectedFileCount, setExpectedFileCount] = useState<number>(0);
    const [multiUploadStatus, setMultiUploadStatus] = useState<string | null>(null);
    
    // State for unsupported files dialog
    const [unsupportedFilesDialogOpen, setUnsupportedFilesDialogOpen] = useState<boolean>(false);
    const [unsupportedFiles, setUnsupportedFiles] = useState<UnsupportedFile[]>([]);
    const [processableFiles, setProcessableFiles] = useState<UnsupportedFile[]>([]);
    const processableRawFilesRef = useRef<File[]>([]);
    const [openReviewAfterCombine, setOpenReviewAfterCombine] = useState<boolean>(false);

    useEffect(() => {
        if (combineDialogOpen) return;
        if (!openReviewAfterCombine) return;
        setOpenReviewAfterCombine(false);
        if (unsupportedFiles.length === 0 && processableFiles.length === 0) return;
        setUnsupportedFilesDialogOpen(true);
    }, [combineDialogOpen, openReviewAfterCombine, unsupportedFiles.length, processableFiles.length]);

    // Hydrate composition title when selected composition is empty view
    useEffect(() => {
        if (!compositionId) return;
        const comps = ProjectAssetsStore.projectCompositions || [];
        const comp = comps.find((c: any) => c.id === compositionId);
        if (comp) {
            const current = comp.title || comp.name || "";
            setTitle(current);
        }
    }, [compositionId, ProjectAssetsStore.projectCompositions]);

    const persistCompositionTitleIfNeeded = async (): Promise<void> => {
        const newName = String(title || "").trim();
        if (!compositionId || !newName) return;

        const comps = ProjectAssetsStore.projectCompositions || [];
        const comp = comps.find((candidate: any) => candidate?.id === compositionId);
        const current = String(comp?.title || comp?.name || "").trim();
        if (newName === current) return;

        try {
            await RenameComposition({ compositionId, newName });
        } catch {}
    };

    const handleRecordCameraClick = () => {
        trackSessionEvent("solo_video_studio_opened");
        setPageMode(PAGE_MODES.RECORDING_STUDIO);
        setRecordingMode(LibrettoRecordingMode.Video);
    }

    const handleRecordMicClick = () => {
        trackSessionEvent("solo_audio_studio_opened");
        setPageMode(PAGE_MODES.RECORDING_STUDIO);
        setRecordingMode(LibrettoRecordingMode.Audio);
    }

    const handleRecordScreenClick = () => {
        trackSessionEvent("solo_screen_studio_opened");
        setPageMode(PAGE_MODES.RECORDING_STUDIO);
        setRecordingMode(LibrettoRecordingMode.Video);
        setIsScreenRecordingMode(true);
    }

    const setResponsiveRecordMenuPosition = (anchorRect: DOMRect, preferredLeftViewport: number) => {
        const rootRect = viewRootRef.current?.getBoundingClientRect();
        if (!rootRect) return;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const horizontalPadding = 8;
        const verticalPadding = 8;
        const menuWidth = Math.min(260, Math.max(200, viewportWidth - horizontalPadding * 2));
        const estimatedMenuHeight = Math.min(240, Math.max(180, viewportHeight - verticalPadding * 2));

        const clampedLeft = Math.min(
            Math.max(preferredLeftViewport, horizontalPadding),
            viewportWidth - menuWidth - horizontalPadding,
        );

        let topInViewport = anchorRect.bottom + 8;
        const bottomLimit = viewportHeight - verticalPadding;
        if (topInViewport + estimatedMenuHeight > bottomLimit) {
            topInViewport = Math.max(verticalPadding, anchorRect.top - estimatedMenuHeight - 8);
        }

        const rootLeft = rootRect.left;
        const rootTop = rootRect.top;
        const rootWidth = rootRect.width;
        const maxLocalLeft = Math.max(horizontalPadding, rootWidth - menuWidth - horizontalPadding);
        const localLeft = Math.min(
            Math.max(clampedLeft - rootLeft, horizontalPadding),
            maxLocalLeft,
        );
        const localTop = Math.max(verticalPadding, topInViewport - rootTop);

        setRecordMenuPos({
            top: localTop,
            left: localLeft,
        });
    };

    // When an uploaded asset appears as a track in the store, auto-create a script clip and add to composition
    useEffect(() => {
        if (!pendingAssetIds.length) return;
        const tracks = ProjectAssetsStore.projectTracks || [];
        const nextAssetId = pendingAssetIds.find((assetId) => tracks.some((t: any) => t.assetId === assetId));
        if (!nextAssetId) return;
        const tr = tracks.find((t: any) => t.assetId === nextAssetId);
        if (!tr) return;
        void (async () => {
            try {
                const trackId = tr.trackId;
                const assetTitle = tr.title || '';
                const isVideo = IsVideoTrackType(tr.trackType);
                const url = await GetTrackUrl({trackId});
                if (!url) return;

	                if (!CompositionService.isInitialized()) {
	                    setPendingAssetIds((prev) => prev.filter((id) => id !== nextAssetId));
	                    return;
	                }

                const scriptLayerClip = await insertTrackIntoScriptLayer({
                    trackId: String(trackId || "").trim(),
                    trackType: isVideo ? "video" : "audio",
                    trackTitle: assetTitle,
                    trackResolution: String(tr.resolution || ""),
                    resolveTrackUrl: async () => url,
                });

                if (scriptLayerClip) {
                    console.log('[CreateCompositionView] Added script layer clip:', scriptLayerClip.id);
                }
                
                setPendingAssetIds((prev) => prev.filter((id) => id !== nextAssetId));
            } catch (e) {
                console.error("Failed to auto-create script clip from uploaded asset:", e);
                setPendingAssetIds((prev) => prev.filter((id) => id !== nextAssetId));
            }
        })();
    }, [pendingAssetIds, ProjectAssetsStore.projectTracks]);

    // Upload a single file and return its asset ID
    const uploadSingleFile = async (file: File): Promise<string | null> => {
                try {
                    const assetId = await UploadAsset({
                        projectId,
                        inputRef: fileInputRef.current,
                        file,
                        projectType: LibrettoProjectTypes.ProjectTypeEditor,
                        filetype: file.type.startsWith('image/') ? "ImageUpload" : (file.type.startsWith('video/') ? "video" : "audio"),
                        languageCode: "auto",
                    });
            return assetId;
                } catch (e) {
                    console.error("Upload failed: filetype=", file.type, "name=", file.name, e);
                    console.error("Upload failed:", e);
            return null;
        }
    };

    const handleFilesSelected = async (files: File[]) => {
        if (!files || files.length === 0) return;

        await persistCompositionTitleIfNeeded();

        // Give immediate UI feedback by opening the Files sidebar so the user can see upload progress.
        openProjectSidebar?.();
        
        const { localOkFiles, serverProcessFiles, rejectedFiles } = await validateFilesForUpload(files);
        const processableUi = serverProcessFiles.map((entry) => entry.display);
        const processableRaw = serverProcessFiles.map((entry) => entry.file);

        const shouldShowReview = processableUi.length > 0 || rejectedFiles.length > 0;
        if (shouldShowReview) {
            setUnsupportedFiles(rejectedFiles);
            setProcessableFiles(processableUi);
            processableRawFilesRef.current = processableRaw;
        }

        if (localOkFiles.length === 0) {
            if (shouldShowReview) setUnsupportedFilesDialogOpen(true);
            return;
        }

        const hasServerProcessingInSelection = processableRaw.length > 0;
        const willOpenCombine = !hasServerProcessingInSelection && localOkFiles.length > 1;
        if (shouldShowReview) {
            if (willOpenCombine) setOpenReviewAfterCombine(true);
            else setUnsupportedFilesDialogOpen(true);
        }
        
        // Single local-first file: upload and add to pendingAssetIds (existing flow)
        if (localOkFiles.length === 1) {
            const assetId = await uploadSingleFile(localOkFiles[0]);
            if (assetId) {
                setPendingAssetIds((prev) => [...prev, assetId]);
            }
            return;
        }
        
        // Keep it simple if server-side processing is involved in this selection:
        // don't offer Sequence combining even if multiple local-first files were selected.
        if (!willOpenCombine) {
            for (const file of localOkFiles) {
                const assetId = await uploadSingleFile(file);
                if (assetId) {
                    setPendingAssetIds((prev) => [...prev, assetId]);
                }
            }
            return;
        }

        // Multiple local-first files: start ALL uploads immediately (parallel, no limit)
        // Show dialog while uploads are in progress.
        setPendingFiles(localOkFiles.map((file) => ({ file })));
        setExpectedFileCount(localOkFiles.length);
        setCombineDialogOpen(true);
        
        // Start all uploads in parallel (no concurrency limit)
        const uploadPromises = localOkFiles.map(async (file, uploadIndex) => {
            const assetId = await uploadSingleFile(file);
            if (!assetId) return null;
            return { assetId, fileName: file.name, uploadIndex };
        });
        const results = await Promise.all(uploadPromises);
        const validUploads = results.filter(
            (r): r is { assetId: string; fileName: string; uploadIndex: number } => r !== null
        );
        
        // Store the uploaded asset IDs - they'll be processed based on user's choice
        setMultiUploadAssets(validUploads);
        console.log('[CreateCompositionView] Multi-file upload complete:', validUploads.length, 'files');
    };

    // Handler: User chose to keep files separate
    const handleKeepSeparate = () => {
        setCombineDialogOpen(false);
        setPendingFiles([]);
        setMultiUploadMode('separate');
        setMultiUploadStatus('Preparing…');
        // The multiUploadAssetIds will be processed by the useEffect below
    };

    // Handler: User chose to combine files into a sequence
    const handleCombineIntoSequence = async () => {
        setCombineDialogOpen(false);
        const files = pendingFiles.map((p) => p.file);
        setPendingFiles([]);
        
        // Create a sequence for these files
        const sequenceTitle = deriveSequenceTitleFromFiles(files);
        setSequenceTitleForMultiUpload(sequenceTitle);
        const sequenceId = await CreateSequence({ projectId, title: sequenceTitle });
        console.log('[CreateCompositionView] Created sequence for multi-upload:', sequenceId);
        
        setSequenceIdForMultiUpload(sequenceId);
        setMultiUploadMode('sequence');
        setMultiUploadStatus('Preparing…');
        // The multiUploadAssetIds will be processed by the useEffect below
    };

    // Process multi-upload assets when mode is selected and all tracks are available
    useEffect(() => {
        if (!multiUploadMode || multiUploadAssets.length === 0) return;
        if (multiUploadProcessingRef.current) return;
        
        const tracks = ProjectAssetsStore.projectTracks || [];

        const stripExt = (name: string) => name.replace(/\.[^/.]+$/, '');
        const hasDigits = (name: string) => /\d/.test(stripExt(name));
        const shouldOrderByFilename = multiUploadAssets.length > 1 && multiUploadAssets.every((a) => hasDigits(a.fileName));
        const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
        const orderedAssets = [...multiUploadAssets].sort((a, b) => {
            if (!shouldOrderByFilename) return a.uploadIndex - b.uploadIndex;
            return collator.compare(a.fileName, b.fileName);
        });
        const orderedAssetIds = orderedAssets.map((a) => a.assetId);
        
        // Check if all uploaded assets have appeared as tracks
        const availableTracks = orderedAssetIds
            .map((assetId) => tracks.find((t: any) => t.assetId === assetId))
            .filter((t): t is any => t !== undefined);
        
        // Wait until all tracks are available
        if (availableTracks.length < orderedAssetIds.length) {
            setMultiUploadStatus('Preparing…');
            return;
        }
        
        console.log('[CreateCompositionView] All tracks available for multi-upload:', availableTracks.length);

        const getTrackDurationSec = (tr: any): number | null => {
            const raw = tr?.duration ?? tr?.Duration ?? tr?.durationSec ?? tr?.durationSecs ?? tr?.media?.durationSec;
            const val = typeof raw === 'number' ? raw : Number(raw);
            if (!Number.isFinite(val) || val <= 0) return null;
            return val;
        };
        
        if (multiUploadMode === 'separate') {
            // "Keep separate" in V2: create ONE script clip with sources arranged sequentially.
            // Wait until durations are available so offsets are stable.
            const durations = availableTracks.map(getTrackDurationSec);
            if (durations.some((d) => d == null)) {
                setMultiUploadStatus('Preparing…');
                return;
            }

            setMultiUploadStatus('Creating…');
            multiUploadProcessingRef.current = true;

            void (async () => {
                try {
                    if (!CompositionService.isInitialized()) {
                        console.warn('[CreateCompositionView] Composition not initialized for keep-separate creation');
                        return;
                    }

                    const comp = CompositionService.composition;

	                    // First VIDEO sets the composition canvas resolution (and thus max export resolution).
	                    const firstVideoTrack = availableTracks.find((t: any) => IsVideoTrackType(t.trackType));
	                    if (firstVideoTrack) {
	                        const m = (firstVideoTrack.resolution || "").match(/(\d+)x(\d+)/);
	                        if (m) {
	                            maybeLockCanvasToFirstVideo(comp, parseInt(m[1], 10), parseInt(m[2], 10));
	                        }
	                    }

                    const compWidth = comp?.width ?? 1920;
                    const compHeight = comp?.height ?? 1080;

                    const inputConfigs: ScriptLayerInputConfig[] = [];
                    let offsetSec = 0;

                    for (let i = 0; i < availableTracks.length; i++) {
                        const tr = availableTracks[i];
                        const durationSec = durations[i] ?? 0;
                        const trackId = tr.trackId;
                        const isVideo = IsVideoTrackType(tr.trackType);
                        const url = await GetTrackUrl({ trackId });
                        if (!url) continue;

                        // Parse video dimensions
                        let trackWidth = 0;
                        let trackHeight = 0;
                        if (isVideo) {
                            const m = (tr.resolution || "").match(/(\d+)x(\d+)/);
                            if (m) {
                                trackWidth = parseInt(m[1], 10);
                                trackHeight = parseInt(m[2], 10);
                            }
                        }

                        const source = new UrlSource(url);
                        const input = new Input({ source, formats: ALL_FORMATS });

                        const config: ScriptLayerInputConfig = {
                            input,
                            assetId: trackId,
                            name: tr.title || '',
                            color: DEFAULT_SOURCE_COLORS[i % DEFAULT_SOURCE_COLORS.length],
                            gain: 1.0,
                            offsetSec,
                        };

                        if (isVideo) {
                            const fitted = fitToCanvas(trackWidth, trackHeight, compWidth, compHeight);
                            config.visual = {
                                x: compWidth / 2,
                                y: compHeight / 2,
                                width: fitted.width,
                                height: fitted.height,
                                alpha: 1.0,
                            };
                        }

                        inputConfigs.push(config);
                        offsetSec += durationSec;
                    }

                    if (inputConfigs.length === 0) {
                        console.warn('[CreateCompositionView] No valid inputs for keep-separate');
                        return;
                    }

                    const scriptLayerClip = await CreateScriptClipForComposition({
                        inputConfigs,
                        composition: comp,
                        stateStore: CompositionStateStore,
                        clipName: title || 'Recording',
                    });

                    if (scriptLayerClip) {
                        await CompositionService.addToScriptLayer(scriptLayerClip);
                        console.log('[CreateCompositionView] Added sequential script clip:', scriptLayerClip.id);

                        // Add scene boundaries at each join between parts (start of each subsequent source)
                        for (let i = 1; i < inputConfigs.length; i++) {
                            const t = Number(inputConfigs[i]?.offsetSec ?? 0);
                            if (Number.isFinite(t) && t > 0) {
                                CompositionService.splitScriptLayer(t, true);
                            }
                        }
                    }
                } catch (e) {
                    console.error('[CreateCompositionView] Failed to create keep-separate script clip:', e);
                } finally {
                    multiUploadProcessingRef.current = false;
                    setMultiUploadAssets([]);
                    setMultiUploadMode(null);
                    setSequenceIdForMultiUpload(null);
                    setSequenceTitleForMultiUpload(null);
                    setExpectedFileCount(0);
                    setMultiUploadStatus(null);
                }
            })();
        } else if (multiUploadMode === 'sequence' && sequenceIdForMultiUpload) {
            // Create a script clip from all the tracks combined
            setMultiUploadStatus('Creating…');
            multiUploadProcessingRef.current = true;
            void (async () => {
                try {
                    if (!CompositionService.isInitialized()) {
                        console.warn('[CreateCompositionView] Composition not initialized for sequence creation');
                        return;
                    }
                    
	                    const comp = CompositionService.composition;

                    // Persist the auto-created sequence so it can be edited/reused later.
                    try {
                        const userId = authContext.getUserId?.();
                        const workspaceId =
                            authContext.getCurrentWorkspaceId?.() ||
                            authContext.getDefaultWorkspaceId?.() ||
                            userId ||
                            null;
                        if (userId) {
                            const layerNames = orderedAssets.map((a) => String(a.fileName ?? "").replace(/\.[^/.]+$/, ""));
                            await populateSequenceFromTracks({
                                sequenceId: sequenceIdForMultiUpload,
                                projectId,
                                userId,
                                workspaceId,
                                tracks: availableTracks,
                                layerNames,
                            });
                        } else {
                            console.warn('[CreateCompositionView] Missing userId; skipping sequence persistence');
                        }
                    } catch (e) {
                        console.warn('[CreateCompositionView] Failed to persist sequence structure for multi-upload', e);
                    }

	                    // First VIDEO sets the composition canvas resolution (and thus max export resolution).
	                    const firstVideoTrack = availableTracks.find((t: any) => IsVideoTrackType(t.trackType));
	                    if (firstVideoTrack) {
	                        const m = (firstVideoTrack.resolution || "").match(/(\d+)x(\d+)/);
	                        if (m) {
	                            maybeLockCanvasToFirstVideo(comp, parseInt(m[1], 10), parseInt(m[2], 10));
	                        }
	                    }

	                    const compWidth = comp?.width ?? 1920;
	                    const compHeight = comp?.height ?? 1080;
                    
                    // Build input configs for all tracks
                    const inputConfigs: ScriptLayerInputConfig[] = [];
                    
                    let sourceIndex = 0;
                    for (const tr of availableTracks) {
                        const trackId = tr.trackId;
                        const isVideo = IsVideoTrackType(tr.trackType);
                        const url = await GetTrackUrl({ trackId });
                        if (!url) continue;
                        
                        // Parse video dimensions
                        let trackWidth = 0;
                        let trackHeight = 0;
                        if (isVideo) {
                            const m = (tr.resolution || "").match(/(\d+)x(\d+)/);
                            if (m) {
                                trackWidth = parseInt(m[1], 10);
                                trackHeight = parseInt(m[2], 10);
                            }
                        }
                        
                        // Create media input
                        const source = new UrlSource(url);
                        const input = new Input({ source, formats: ALL_FORMATS });
                        
                        // Build config for this source
                        const config: ScriptLayerInputConfig = {
                            input,
                            assetId: trackId,
                            name: tr.title || '',
                            color: DEFAULT_SOURCE_COLORS[sourceIndex % DEFAULT_SOURCE_COLORS.length],
                            gain: 1.0,
                        };
                        sourceIndex++;
                        
	                        // Add visual properties if video
	                        if (isVideo) {
	                            const fitted = fitToCanvas(trackWidth, trackHeight, compWidth, compHeight);
	                            config.visual = {
	                                x: compWidth / 2,
	                                y: compHeight / 2,
	                                width: fitted.width,
	                                height: fitted.height,
	                                alpha: 1.0,
	                            };
	                        }
                        
                        inputConfigs.push(config);
                    }
                    
                    if (inputConfigs.length === 0) {
                        console.warn('[CreateCompositionView] No valid inputs for sequence');
                        return;
                    }
                    
	                    // Create a single script clip with all inputs (multi-track sequence)
	                    const scriptLayerClip = await CreateScriptClipForComposition({
	                        inputConfigs,
	                        composition: comp,
                        stateStore: CompositionStateStore,
                        clipName: sequenceTitleForMultiUpload || title || 'Multi-track Recording',
                    });
                    
                    if (scriptLayerClip) {
                        await CompositionService.addToScriptLayer(scriptLayerClip);
                        console.log('[CreateCompositionView] Added multi-track script clip:', scriptLayerClip.id);
                    }
                } catch (e) {
                    console.error('[CreateCompositionView] Failed to create sequence script clip:', e);
                } finally {
                    multiUploadProcessingRef.current = false;
                    // Clear multi-upload state
                    setMultiUploadAssets([]);
                    setMultiUploadMode(null);
                    setSequenceIdForMultiUpload(null);
                    setSequenceTitleForMultiUpload(null);
                    setExpectedFileCount(0);
                    setMultiUploadStatus(null);
                }
            })();
        }
    }, [
        multiUploadMode,
        multiUploadAssets,
        sequenceIdForMultiUpload,
        sequenceTitleForMultiUpload,
        ProjectAssetsStore.projectTracks,
        GetTrackUrl,
        title,
        projectId,
        authContext.authState?.userId,
    ]);

    const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files ?? []);
        void handleFilesSelected(files);
    };

    const onBrowse = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);
        void handleFilesSelected(files);
        // reset so picking the same file(s) again triggers onChange
        e.target.value = "";
    };

    // Styles inspired by AgenticUploadContentCard, but inline
    const container = {
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '16px',
        width: '100%',
        maxWidth: { xs: '100%', sm: '600px', md: '620px' },
        padding: { xs: '0px', sm: '8px 12px' },
    };

    const titleRow = {
        alignSelf: 'stretch',
        display: 'flex',
        justifyContent: 'flex-start',
        paddingLeft: { xs: '2px', sm: '6px' },
    };

    const dropArea = {
        alignSelf: 'stretch',
        display: 'grid',
        placeContent: 'center',
        borderRadius: '10px',
        border: '1px dashed #BFBFBF',
        height: '360px',
        background: '#FFFFFF',
        cursor: 'pointer',
    };

    const helperText = {
        color: '#666',
        fontSize: '13px',
        textAlign: 'center' as const,
        marginTop: '8px',
        paddingLeft: '8px',
        paddingRight: '8px',
    };

    const uploadAreaStyle = {
        display: 'flex',
        width: '100%',
        maxWidth: '100%',
        minHeight: { xs: "220px", sm: "250px", md: "280px" },
        flexDirection: 'column',
        padding: { xs: "16px", sm: "20px", md: "24px" },
        justifyContent: 'center',
        alignItems: 'center',
        gap: "12px",
        alignSelf: 'stretch',
        borderRadius: "6px",
        border: "1px dashed #BFBFBF",
        cursor: "pointer",
        // Hover styles
        ':hover': {
            backgroundColor: "#F5F5F5",
            borderColor: "#999999",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)"
        }
    }

    return (
        <Box ref={viewRootRef} sx={{
            display: 'flex',
            width: '100%',
            height: '100%',
            flexDirection: "column",
            alignItems: 'center',
            paddingTop: { xs: "24px", sm: "32px", md: "50px" },
            paddingX: { xs: "12px", sm: "20px", md: "24px" },
            justifyContent: 'flex-start',
            position: 'relative',
            overflow: 'visible',
        }}>
            <Box sx={container}>
                <Box sx={titleRow}>
                    <TextField
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Name your creation"
                        variant="standard"
                        onBlur={async () => {
                            const newName = title.trim();
                            if (compositionId && newName.length > 0) {
                                const comps = ProjectAssetsStore.projectCompositions || [];
                                const comp = comps.find((c: any) => c.id === compositionId);
                                const current = comp ? (comp.title || comp.name || "") : "";
                                if (newName !== current) {
                                    try { await RenameComposition({ compositionId, newName }); } catch {}
                                }
                            }
                        }}
                        onKeyDown={async (e) => {
                            if (e.key === 'Enter') {
                                const newName = title.trim();
                                if (compositionId && newName.length > 0) {
                                    const comps = ProjectAssetsStore.projectCompositions || [];
                                    const comp = comps.find((c: any) => c.id === compositionId);
                                    const current = comp ? (comp.title || comp.name || "") : "";
                                    if (newName !== current) {
                                        try { await RenameComposition({ compositionId, newName }); } catch {}
                                    }
                                }
                            }
                        }}
                        InputProps={{disableUnderline: true, autoComplete: "off", autoFocus: false, autoCapitalize: "off",  style: {fontSize: 26}}}
                        sx={{width: { xs: '100%', sm: '650px' }, maxWidth: '100%', marginBottom: "20px"}}
                    />
                </Box>
                <Box
                    sx={uploadAreaStyle}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={onDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <Box sx={{textAlign: 'center'}}>
                        <div style={{fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#333'}}>Upload file
                        </div>
                        <div style={helperText as any}>Click to browse or drag & drop supported files here</div>
                    </Box>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/*,audio/*,image/*"
                        multiple
                        style={{display: 'none'}}
                        onChange={onBrowse}
                    />
                </Box>
                {multiUploadStatus && (
                    <Typography sx={{ fontFamily: 'Inter', fontSize: 13, color: '#6B7280', mt: 1.5 }}>
                        {multiUploadStatus}
                    </Typography>
                )}
            </Box>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: { xs: 'nowrap', sm: 'wrap' },
                alignItems: 'stretch',
                justifyContent: 'center',
                gap: { xs: '10px', sm: '20px' },
                width: '100%',
                maxWidth: { xs: '100%', sm: '620px' },
                mt: { xs: 0.5, sm: 1 },
            }}>
                <Box
                    ref={recordButtonRef}
                    sx={{
                        flex: { xs: "1 1 auto", sm: "1 1 0" },
                        width: '100%',
                        maxWidth: { xs: '100%', sm: '300px' },
                    }}
                >
                    <SelectableCardButton
                        label={"Record"}
                        icon={<RadioButtonCheckedOutlined sx={{ color: "red" }} />}
                        onClick={() => {
                            if (recordMenuOpen) {
                                setRecordMenuOpen(false);
                                return;
                            }
                            const rect = recordButtonRef.current?.getBoundingClientRect();
                            if (rect) {
                                setResponsiveRecordMenuPosition(rect, rect.left);
                            }
                            setRecordMenuOpen(true);
                        }}
                        selected={false}
                        disabled={false}
                        large={true}
                        centered={true}
                        ref={null}
                    />
                </Box>

                <Box
                    sx={{
                        flex: { xs: "1 1 auto", sm: "1 1 0" },
                        width: '100%',
                        maxWidth: { xs: '100%', sm: '300px' },
                    }}
                >
                    <SelectableCardButton
                        label={"Upload from mobile"}
                        icon={<SendToMobileOutlined fontSize={"small"} />}
                        onClick={() => {
                            setRecordMenuOpen(false);
                            trackSessionEvent("mobile_upload_qr_opened");
                            setMobileUploadDialogOpen(true);
                        }}
                        selected={false}
                        disabled={false}
                        large={true}
                        centered={true}
                        ref={null}
                    />
                </Box>

                <Box
                    sx={{
                        flex: { xs: "1 1 auto", sm: "0 0 100%" },
                        width: "100%",
                        maxWidth: "100%",
                        "& .selectable-card-button": { maxWidth: "100%" },
                    }}
                >
                    <SelectableCardButton
                        label="Import from Zoom"
                        icon={<CloudDownloadOutlined fontSize="small" sx={{ color: "#2D8CFF" }} />}
                        onClick={() => {
                            setRecordMenuOpen(false);
                            onImportFromZoom?.();
                        }}
                        selected={false}
                        disabled={!onImportFromZoom}
                        large={true}
                        centered={true}
                        ref={null}
                    />
                </Box>
            </Box>

            {recordMenuOpen && (
                <RecordSomethingMenu
                    setOpen={setRecordMenuOpen}
                    handleRecordScreenClick={() => handleRecordScreenClick()}
                    handleRecordCameraClick={() => handleRecordCameraClick()}
                    handleRecordAudioOnlyClick={() => handleRecordMicClick()}
                    handleRecordWithOthersClick={() => {
                        trackSessionEvent("entered_studio_lobby");
                        const params = new URLSearchParams();
                        params.set("studioOrigin", "editor");
                        if (compositionId) {
                            params.set("originCompositionId", compositionId);
                        }
                        navigate(`/studio/${projectId}?${params.toString()}`);
                    }}
                    top={recordMenuPos.top}
                    left={recordMenuPos.left}
                />
            )}

            <MobileUploadDialog
                open={mobileUploadDialogOpen}
                onClose={() => setMobileUploadDialogOpen(false)}
                projectId={projectId}
            />

            {/* Combine Files Dialog - shown when multiple files are selected */}
            <CombineFilesDialog
                isOpen={combineDialogOpen}
                onClose={() => {
                    setCombineDialogOpen(false);
                    setPendingFiles([]);
                }}
                files={pendingFiles}
                onKeepSeparate={handleKeepSeparate}
                onCombineIntoSequence={handleCombineIntoSequence}
            />
            
            {/* Unsupported Files Dialog - shown when unsupported files are detected */}
            <UnsupportedFilesDialog
                isOpen={unsupportedFilesDialogOpen}
                onClose={() => {
                    setUnsupportedFilesDialogOpen(false);
                    setUnsupportedFiles([]);
                    setProcessableFiles([]);
                    processableRawFilesRef.current = [];
                }}
                files={unsupportedFiles}
                processableFiles={processableFiles}
                onUploadProcessable={async () => {
                    const toUpload = [...(processableRawFilesRef.current ?? [])];
                    setUnsupportedFilesDialogOpen(false);
                    setUnsupportedFiles([]);
                    setProcessableFiles([]);
                    processableRawFilesRef.current = [];

                    // Give immediate UI feedback by opening the Files sidebar so the user can see upload progress.
                    openProjectSidebar?.();

                    for (const file of toUpload) {
                        await UploadNonLocalFirstAsset({
                            projectId,
                            projectType: LibrettoProjectTypes.ProjectTypeEditor,
                            file,
                            filetype: "video",
                            languageCode: "auto",
                        });
                    }
                }}
            />
        </Box>
    );
};

export default CreateCompositionView;
